import { voiceBus } from './index';

/**
 * Spoken acknowledgements.
 *
 * Every other sound in the game is synthesised at runtime. These are the one
 * exception, because there is no way to synthesise a voice, so they are the
 * only audio files the project ships.
 *
 * The whole module is built to work with none of them present. Files are
 * discovered from the assets folder at build time, so a category with no
 * recordings simply never speaks and nothing anywhere needs a guard. That
 * matters: the recordings are generated by `tools/generate-voices.mjs` against
 * a paid API, so a fresh clone has none of them and must still run.
 *
 * Add files as `assets/audio/voices/<category>/<anything>.mp3`. The file name
 * is free-form; only the folder decides when a line is used.
 */

export const VoiceLine = {
  /**
   * A marching column of yours was selected. Villages deliberately do not
   * speak: clicking them is how you read the map, and answering every click
   * makes the acknowledgement worthless.
   */
  SELECT: 'select',
  /** Troops sent somewhere that is not a fight. */
  MOVE: 'move',
  /** Troops sent to attack or raid. */
  ATTACK: 'attack',
  /** An order was called off. */
  CANCEL: 'cancel',
  /**
   * An order was taken. "At once, sir."
   *
   * A real recorded voice rather than the synthesised pack: this is the line a
   * player hears most, and the one where a text-to-speech read is most
   * obviously a machine.
   */
  CONFIRM: 'confirm',
  /** The order could not be given at all. */
  DENIED: 'denied',
  /** Construction started. */
  BUILD: 'build',
  /** Troops ordered to the training yard, which is not the same as building. */
  RECRUIT: 'recruit',
  /** Something finished. */
  COMPLETE: 'complete',
  /** Occasional, unprompted, while sitting in the village. */
  IDLE: 'idle',
} as const;
export type VoiceLine = (typeof VoiceLine)[keyof typeof VoiceLine];

/**
 * Every mp3 under the voices folder, grouped by its parent directory.
 *
 * `eager` so the grouping happens once at module load rather than on the first
 * line spoken. These are URLs, not the audio itself, so it costs nothing.
 */
const FILES = import.meta.glob('../assets/audio/voices/*/*.mp3', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

const byCategory = new Map<string, string[]>();
for (const [path, url] of Object.entries(FILES)) {
  const category = path.split('/').at(-2);
  if (!category) continue;
  const list = byCategory.get(category) ?? [];
  list.push(url);
  byCategory.set(category, list);
}
// Stable order, so the no-immediate-repeat rule behaves the same every load.
for (const list of byCategory.values()) list.sort();

/** True when at least one recording exists for that category. */
export function hasVoice(line: VoiceLine): boolean {
  return (byCategory.get(line)?.length ?? 0) > 0;
}

export function voicePackSize(): number {
  let n = 0;
  for (const list of byCategory.values()) n += list.length;
  return n;
}

const buffers = new Map<string, AudioBuffer>();
const pending = new Map<string, Promise<AudioBuffer | null>>();

async function loadBuffer(ctx: AudioContext, url: string): Promise<AudioBuffer | null> {
  const cached = buffers.get(url);
  if (cached) return cached;

  const already = pending.get(url);
  if (already) return already;

  const job = (async () => {
    try {
      const res = await fetch(url);
      const bytes = await res.arrayBuffer();
      const buffer = await ctx.decodeAudioData(bytes);
      buffers.set(url, buffer);
      return buffer;
    } catch {
      // A missing or corrupt line must never take an order down with it.
      return null;
    } finally {
      pending.delete(url);
    }
  })();

  pending.set(url, job);
  return job;
}

/** The last file played per category, so the same clip never repeats twice. */
const lastUrl = new Map<string, string>();

function pick(line: VoiceLine): string | null {
  const list = byCategory.get(line);
  if (!list?.length) return null;
  if (list.length === 1) return list[0]!;

  const previous = lastUrl.get(line);
  let chosen = list[Math.floor(Math.random() * list.length)]!;
  if (chosen === previous) {
    // One nudge is enough; with three or more variations this is rare.
    chosen = list[(list.indexOf(chosen) + 1) % list.length]!;
  }
  lastUrl.set(line, chosen);
  return chosen;
}

/**
 * One voice at a time.
 *
 * Two men talking over each other sounds broken rather than busy, so a new
 * line cuts the one still speaking. The floor stops a double-click producing a
 * stutter of the same half-word.
 */
const MIN_GAP_MS = 220;
let current: AudioBufferSourceNode | null = null;
let lastSpokeAt = -Infinity;

export function speak(line: VoiceLine): void {
  const bus = voiceBus();
  if (!bus) return;

  const now = performance.now();
  if (now - lastSpokeAt < MIN_GAP_MS) return;

  const url = pick(line);
  if (!url) return;
  lastSpokeAt = now;

  void loadBuffer(bus.ctx, url).then((buffer) => {
    if (!buffer) return;
    // Re-checked after the await: the player may have muted while it decoded.
    const live = voiceBus();
    if (!live) return;

    try {
      current?.stop();
    } catch {
      // Already finished. Nothing to stop.
    }

    const source = live.ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(live.out);
    source.onended = () => {
      if (current === source) current = null;
    };
    current = source;
    source.start();
  });
}

/**
 * Warm the cache for lines about to be needed.
 *
 * Called when the map opens, so the first order does not answer a beat late
 * while its file downloads.
 */
export function preloadVoices(lines: VoiceLine[]): void {
  const bus = voiceBus();
  if (!bus) return;
  for (const line of lines) {
    for (const url of byCategory.get(line) ?? []) void loadBuffer(bus.ctx, url);
  }
}
